import {
  Avatar,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { IdleWarning } from "../../common-ui/IdleWarning";
import { useUserStats } from "./userUtils";
import { APIError } from "../../common-ui/APIError";
import BarChart from "@mui/icons-material/BarChart";
import { useTranslation } from "react-i18next";
import { ReactNode } from "react";

type StatsCardProps = {
  cardTitle: string;
  children: ReactNode;
};

export const StatsCard = (props: StatsCardProps) => {
  return (
    <Grid xs={12} sm={5} md={4} lg={3}>
      <Card sx={{ height: 76 }}>
        <CardContent
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            height: "100%",
            padding: "8px 12px",
            "&:last-child": { paddingBottom: "8px" }
          }}
        >
          <Avatar sx={{ bgcolor: "#F9F4F5", width: 32, height: 32 }}>
            <BarChart sx={{ fontSize: 18 }} color="primary" />
          </Avatar>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              minWidth: 0,
              "& .MuiTypography-root": { lineHeight: 1.3 }
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {props.cardTitle}
            </Typography>
            {props.children}
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};

export const UserStats = ({ userId }: { userId?: string }) => {
  const { t } = useTranslation();
  const { stats, status, error } = useUserStats(userId);

  if (status === "idle") return <IdleWarning />;
  if (status === "loading") return <CircularProgress />;
  if (status === "failed")
    return <APIError error={error || "Unknown error fetching user stats"} />;

  const percentCompleted = stats?.percentCompleted;
  const percentCompletedString = percentCompleted
    ? `${percentCompleted}%`
    : "N/A";

  const averageResponseTime = stats?.averageResponseTime;
  const averageResponseTimeString = averageResponseTime
    ? `${averageResponseTime} days`
    : "N/A";

  const assessmentsCreated = stats?.assessmentsCreated;
  return (
    <Grid container sx={{ py: 0.5, rowGap: 0.25, columnGap: 0.5 }}>
      <Grid xs={12} sx={{ marginBottom: 0 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, margin: 0, display: "block" }}
        >
          {t("Stats across all programs")}
        </Typography>
      </Grid>
      <StatsCard cardTitle={t("All time")}>
        <Typography variant="body2">{`${t(
          "Assessments Created:"
        )} ${assessmentsCreated}`}</Typography>
        <Typography variant="body2">{`Completed: ${percentCompletedString}`}</Typography>
      </StatsCard>
      <StatsCard cardTitle="Last 90 Days">
        {/* Dev Note: Something about the first string here is breaking i18next */}
        {/* Need to figure out what's going on for translation */}
        <Typography variant="body2">{`${t(
          "Avg Response Time:"
        )} ${averageResponseTimeString}`}</Typography>
      </StatsCard>
    </Grid>
  );
};
